import {
	HttpClient,
	HttpContextToken,
	HttpErrorResponse,
	HttpInterceptorFn,
	HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, exhaustMap, take, tap, throwError } from 'rxjs';

import { isRefreshAuthFailure, markSessionExpiredHandled } from '@core/http/refresh-auth-error';
import type { ManagerProfileResponse } from '@core/models/auth.models';
import { AuthService } from '@core/services/auth.service';
import { ManagerProfileService } from '@core/services/manager-profile.service';
import { SessionService } from '@core/services/session.service';

/** Set on a retried request so a second 401 cannot trigger another refresh. */
export const AUTH_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

/** Auth endpoints that must not trigger an automatic refresh attempt. */
const REFRESH_EXCLUDED_SEGMENTS = [
	'/auth/login',
	'/auth/logout',
	'/auth/forgot-password',
	'/auth/reset-password',
	'/auth/refresh',
] as const;

function isRefreshExcludedRequest(url: string): boolean {
	return REFRESH_EXCLUDED_SEGMENTS.some((segment) => url.includes(segment));
}

function isProfileRequest(url: string): boolean {
	return url.includes('/auth/me');
}

function isManagerProfileResponse(body: unknown): body is ManagerProfileResponse {
	return typeof body === 'object' && body !== null && 'userId' in body && 'permissions' in body;
}

function readResponseBody(body: unknown): unknown {
	return body instanceof HttpResponse ? body.body : body;
}

function cloneWithAccessToken(req: Parameters<HttpInterceptorFn>[0], token: string) {
	return req.clone({
		setHeaders: {
			Authorization: `Bearer ${token}`,
		},
		context: req.context.set(AUTH_REFRESH_RETRIED, true),
	});
}

/**
 * On 401, rotates the refresh token and retries the original request once.
 * Placed just before the loader interceptor so failed requests can recover
 * before the global error alert fires.
 */
export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
	const auth = inject(AuthService);
	const profileService = inject(ManagerProfileService);
	const session = inject(SessionService);
	const http = inject(HttpClient);

	return next(req).pipe(
		catchError((error: unknown) => {
			if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
				return throwError(() => error);
			}

			if (
				req.context.get(AUTH_REFRESH_RETRIED) ||
				isRefreshExcludedRequest(req.url) ||
				!auth.refreshToken()
			) {
				return throwError(() => error);
			}

			return auth.refreshAccessToken().pipe(
				take(1),
				catchError((refreshError: unknown) => {
					if (isRefreshAuthFailure(refreshError)) {
						session.expireSession();
						return throwError(() => markSessionExpiredHandled(refreshError));
					}

					return throwError(() => refreshError);
				}),
				exhaustMap(() => {
					profileService.invalidate();

					const token = auth.accessToken();
					if (!token) {
						return throwError(() => error);
					}

					let profileRecoveryStarted = false;

					return http.request(cloneWithAccessToken(req, token)).pipe(
						tap((response) => {
							if (profileRecoveryStarted) {
								return;
							}

							profileRecoveryStarted = true;
							const body = readResponseBody(response);

							if (isProfileRequest(req.url)) {
								if (isManagerProfileResponse(body)) {
									profileService.hydrateProfile(body);
								}

								return;
							}

							profileService.ensureLoaded().subscribe();
						}),
					);
				}),
			);
		}),
	);
};
